/**
 * Similar context confirmation dialog component.
 *
 * Displays similar contexts found during creation/update and allows
 * user to confirm saving despite similarity.
 *
 * @module SimilarContextConfirmationDialog
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface SimilarContext {
  id: string;
  name: string;
  context_type: string;
  content: string;
  similarity: number;
}

interface SimilarContextConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  similarContexts: SimilarContext[];
  maxSimilarity: number;
  threshold: number;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function SimilarContextConfirmationDialog({
  open,
  onOpenChange,
  similarContexts,
  maxSimilarity,
  threshold,
  message,
  onConfirm,
  onCancel,
}: Readonly<SimilarContextConfirmationDialogProps>) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-ui flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Similar Contexts Found
          </DialogTitle>
          <DialogDescription className="font-ui">{message}</DialogDescription>
        </DialogHeader>

        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="font-ui">
            Maximum similarity: <strong>{(maxSimilarity * 100).toFixed(1)}%</strong>{' '}
            (threshold: {(threshold * 100).toFixed(1)}%)
            <br />
            Found {similarContexts.length} similar context
            {similarContexts.length === 1 ? '' : 's'}
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <h3 className="font-ui text-sm font-semibold">Similar Contexts</h3>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {similarContexts.map((context, index) => (
                <Card
                  key={context.id || index}
                  className="border-destructive/20 bg-destructive/5"
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-ui text-sm font-medium">
                        {context.name || `Context ${index + 1}`}
                      </CardTitle>
                      <span className="font-ui text-xs font-semibold text-destructive bg-destructive/10 px-2 py-1 rounded">
                        {(context.similarity * 100).toFixed(1)}% similar
                      </span>
                    </div>
                    <div className="font-ui text-xs text-muted-foreground">
                      ID: {context.id} | Type: {context.context_type}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="font-ui text-sm text-foreground">
                      <div className="font-ui font-medium mb-1">Content</div>
                      <div className="font-ui whitespace-pre-wrap break-words bg-background p-2 rounded border max-h-32 overflow-y-auto">
                        {context.content || 'No content available'}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={handleCancel}>
            <span className="font-ui">Cancel</span>
          </Button>
          <Button variant="default" onClick={handleConfirm}>
            <span className="font-ui">Save Anyway</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
